/**
 * ComparisonTable Component
 *
 * Sortable table comparing departments, operators, or SOPs by scores.
 * Displays rank, name, all score categories, trend, and change percentage.
 */

import { Link } from 'react-router-dom';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  AlertCircle,
  ChevronUp,
  ChevronDown,
  ArrowUpDown,
} from 'lucide-react';
import type { ComparisonItem } from '../../../hooks/useAnalytics';
import { getScoreColor } from '../../../lib/score-colors';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';

interface ComparisonTableProps {
  data: ComparisonItem[];
  compareBy: 'department' | 'operator' | 'sop';
  isLoading?: boolean;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSort: (column: string) => void;
}

type SortableColumn = 'rank' | 'overall' | 'compliance' | 'efficiency' | 'safety' | 'assessmentCount' | 'change';

const COLUMNS: { key: SortableColumn; label: string; align?: 'left' | 'center' | 'right' }[] = [
  { key: 'rank', label: '#', align: 'center' },
  { key: 'overall', label: 'Overall', align: 'center' },
  { key: 'compliance', label: 'Compliance', align: 'center' },
  { key: 'efficiency', label: 'Efficiency', align: 'center' },
  { key: 'safety', label: 'Safety', align: 'center' },
  { key: 'assessmentCount', label: 'Assessments', align: 'center' },
  { key: 'change', label: 'Change', align: 'center' },
];

export function ComparisonTable({
  data,
  compareBy,
  isLoading,
  sortBy,
  sortOrder,
  onSort,
}: ComparisonTableProps) {

  // Get trend icon
  const TrendIcon = ({ trend }: { trend: string }) => {
    switch (trend) {
      case 'improving':
        return <TrendingUp size={14} className="text-emerald-500" />;
      case 'declining':
        return <TrendingDown size={14} className="text-red-500" />;
      case 'stable':
        return <Minus size={14} className="text-gray-400" />;
      default:
        return <AlertCircle size={12} className="text-gray-300" />;
    }
  };

  // Get link path based on compareBy type
  const getItemLink = (item: ComparisonItem) => {
    switch (compareBy) {
      case 'operator':
        return `/operators/${item.id}`;
      case 'sop':
        return `/sops/${item.id}`;
      case 'department':
        return `/settings/departments?highlight=${item.id}`;
      default:
        return '#';
    }
  };

  // Sort indicator
  const SortIndicator = ({ column }: { column: string }) => {
    if (sortBy !== column) {
      return <ArrowUpDown size={14} className="text-gray-300" />;
    }
    return sortOrder === 'asc' ? (
      <ChevronUp size={14} className="text-guild-green" />
    ) : (
      <ChevronDown size={14} className="text-guild-green" />
    );
  };

  // Column label based on compareBy
  const getNameColumnLabel = () => {
    switch (compareBy) {
      case 'department':
        return 'Department';
      case 'operator':
        return 'Operator';
      case 'sop':
        return 'SOP';
      default:
        return 'Name';
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex h-64 flex-col items-center justify-center text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100">
          <svg
            className="h-8 w-8 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        </div>
        <p className="mt-4 text-sm font-medium text-gray-900">No comparison data available</p>
        <p className="mt-1 text-sm text-gray-500">
          Complete some assessments to see comparative performance.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50/50">
            {/* Rank Column */}
            <th className="px-4 py-3 text-center">
              <button
                onClick={() => onSort('rank')}
                className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-700"
              >
                #
                <SortIndicator column="rank" />
              </button>
            </th>

            {/* Name Column (not sortable) */}
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
              {getNameColumnLabel()}
            </th>

            {/* Score Columns */}
            {COLUMNS.filter((col) => col.key !== 'rank').map((column) => (
              <th key={column.key} className={`px-4 py-3 text-${column.align || 'center'}`}>
                <button
                  onClick={() => onSort(column.key)}
                  className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-700"
                >
                  {column.label}
                  <SortIndicator column={column.key} />
                </button>
              </th>
            ))}

            {/* Trend Column */}
            <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">
              Trend
            </th>
          </tr>
        </thead>

        <tbody className="divide-y divide-gray-100">
          {data.map((item) => (
            <tr
              key={item.id}
              className="transition-colors duration-150 hover:bg-guild-mint/5"
            >
              {/* Rank */}
              <td className="px-4 py-3 text-center">
                <span
                  className={`
                    inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold
                    ${item.rank === 1 ? 'bg-amber-100 text-amber-700' : ''}
                    ${item.rank === 2 ? 'bg-gray-200 text-gray-700' : ''}
                    ${item.rank === 3 ? 'bg-orange-100 text-orange-700' : ''}
                    ${item.rank > 3 ? 'bg-gray-50 text-gray-500' : ''}
                  `}
                >
                  {item.rank}
                </span>
              </td>

              {/* Name */}
              <td className="px-4 py-3">
                <Link
                  to={getItemLink(item)}
                  className="font-medium text-gray-900 transition-colors hover:text-guild-green"
                >
                  {item.name}
                </Link>
              </td>

              {/* Overall Score */}
              <td className="px-4 py-3 text-center">
                <span
                  className={`
                    inline-flex min-w-[48px] items-center justify-center rounded-lg px-2 py-1
                    text-sm font-semibold
                    ${getScoreColor(item.overall, 'badge')}
                  `}
                >
                  {item.overall !== null ? item.overall.toFixed(0) : '—'}
                </span>
              </td>

              {/* Compliance Score */}
              <td className="px-4 py-3 text-center">
                <span className={`text-sm font-medium ${getScoreColor(item.compliance, 'text')}`}>
                  {item.compliance !== null ? item.compliance.toFixed(0) : '—'}
                </span>
              </td>

              {/* Efficiency Score */}
              <td className="px-4 py-3 text-center">
                <span className={`text-sm font-medium ${getScoreColor(item.efficiency, 'text')}`}>
                  {item.efficiency !== null ? item.efficiency.toFixed(0) : '—'}
                </span>
              </td>

              {/* Safety Score */}
              <td className="px-4 py-3 text-center">
                <span className={`text-sm font-medium ${getScoreColor(item.safety, 'text')}`}>
                  {item.safety !== null ? item.safety.toFixed(0) : '—'}
                </span>
              </td>

              {/* Assessment Count */}
              <td className="px-4 py-3 text-center">
                <span className="text-sm text-gray-600">{item.assessmentCount}</span>
              </td>

              {/* Change */}
              <td className="px-4 py-3 text-center">
                {item.change !== null ? (
                  <span
                    className={`
                      inline-flex items-center gap-0.5 text-sm font-medium
                      ${item.change > 0 ? 'text-emerald-600' : ''}
                      ${item.change < 0 ? 'text-red-600' : ''}
                      ${item.change === 0 ? 'text-gray-400' : ''}
                    `}
                  >
                    {item.change > 0 ? '+' : ''}
                    {item.change.toFixed(1)}%
                  </span>
                ) : (
                  <span className="text-sm text-gray-400">—</span>
                )}
              </td>

              {/* Trend */}
              <td className="px-4 py-3 text-center">
                <div className="flex items-center justify-center">
                  <TrendIcon trend={item.trend} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default ComparisonTable;
